import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MailCheck } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_pitch-connect-40/artifacts/yd54d9mb_D6EC86DD-43FE-4578-97EA-A54FBCABC788.png";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      setSent(true);
    } catch (err) {
      // Backend intentionally returns a generic response, but still show any
      // rate-limit / validation errors that do surface.
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10 bg-footy-bg grain">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="logo-link">
          <img src={LOGO} alt="" className="w-14 h-14 rounded-full" />
          <span className="font-display text-3xl tracking-wider">FOOTYMATCH</span>
        </Link>
        <div className="footy-card p-7">
          {sent ? (
            <div className="text-center" data-testid="reset-sent">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mb-4">
                <MailCheck size={30} />
              </div>
              <h1 className="font-display text-3xl uppercase tracking-tight">Check your inbox</h1>
              <p className="text-slate-400 mt-3 text-sm leading-relaxed">
                If <span className="text-white font-semibold">{email}</span> is registered, we&apos;ve sent a reset link.
                It&apos;ll expire in 30 minutes. Check your spam folder if you don&apos;t see it soon.
              </p>
              <button onClick={()=>nav("/login")} className="footy-btn-secondary w-full mt-6" data-testid="reset-back-to-login">
                <ArrowLeft size={16} /> Back to log in
              </button>
              <button onClick={()=>{ setSent(false); setEmail(""); }} className="text-xs text-slate-500 hover:text-white mt-4 uppercase tracking-widest" data-testid="reset-send-another">
                Send to a different email
              </button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-4xl uppercase tracking-tight">Forgot password?</h1>
              <p className="text-slate-400 mt-2 text-sm">Pop your email in and we&apos;ll send a reset link.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="footy-label">Email</label>
                  <input type="email" required autoFocus value={email} onChange={(e)=>setEmail(e.target.value)}
                    placeholder="you@example.com" className="footy-input" data-testid="forgot-email" />
                </div>
                <button type="submit" disabled={loading || !email} className="footy-btn-primary w-full" data-testid="forgot-submit">
                  {loading ? "Sending..." : "Send reset link"}
                </button>
              </form>
              <p className="text-sm text-slate-400 mt-6 text-center">
                Remembered it? <Link to="/login" className="text-footy-red font-semibold" data-testid="link-login">Back to log in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
